import { GameState } from '@brainfart/shared';

// "Awards" that make the END of a game a screenshot moment. Each multiplayer
// award goes to exactly one player (a player can win several); solo awards are
// derived from a single run. All computed client-side from data already in
// game state / the run summary — no server changes.

export interface SoloRunSummary {
  total: number;
  correct: number;
  bestStreak: number;
  avgAnswerMs: number;
  timeLimitMs: number;
}

export interface Award {
  emoji: string;
  label: string;
}

// One verdict ladder for every single-player results screen (solo + daily).
export function verdictFor(accuracy: number): string {
  if (accuracy === 1) return 'Quiz Champion 🏆';
  if (accuracy >= 0.75) return 'Quiz Whiz 🧠';
  if (accuracy >= 0.5) return 'Solid Score';
  if (accuracy >= 0.25) return 'Warming Up';
  return 'Better luck next quiz';
}

// Only things the verdict doesn't already say (accuracy is the verdict's job).
export function soloAwards(run: SoloRunSummary): Award[] {
  const awards: Award[] = [];
  if (run.bestStreak >= 4 && run.correct < run.total) {
    awards.push({ emoji: '🔥', label: `${run.bestStreak} in a row` });
  }
  // Fast and at least half right: a speed badge on a mostly missed quiz reads as a jab.
  if (run.total >= 3 && run.correct / run.total >= 0.5 && run.avgAnswerMs > 0 && run.avgAnswerMs < run.timeLimitMs * 0.35) {
    awards.push({ emoji: '⚡', label: 'Speed Demon' });
  }
  return awards;
}

interface PlayerTally {
  id: string;
  answered: number;
  correct: number;
  wrong: number;
  rankSum: number;
  rankCount: number;
}

export function multiplayerAwards(game: GameState): Map<string, Award[]> {
  const tallies = new Map<string, PlayerTally>();
  const ensure = (id: string): PlayerTally => {
    let t = tallies.get(id);
    if (!t) {
      t = { id, answered: 0, correct: 0, wrong: 0, rankSum: 0, rankCount: 0 };
      tallies.set(id, t);
    }
    return t;
  };

  for (const round of game.rounds) {
    for (const [pid, correct] of Object.entries(round.correctness || {})) {
      const t = ensure(pid);
      t.answered += 1;
      if (correct) t.correct += 1;
      else t.wrong += 1;
    }
    for (const [pid, rank] of Object.entries(round.rankings || {})) {
      const t = ensure(pid);
      if (typeof rank === 'number' && rank > 0) {
        t.rankSum += rank;
        t.rankCount += 1;
      }
    }
  }

  const players = [...tallies.values()].filter(t => t.answered > 0);
  const result = new Map<string, Award[]>();
  const give = (id: string, award: Award) => {
    const list = result.get(id) || [];
    list.push(award);
    result.set(id, list);
  };

  if (players.length === 0) return result;

  // 🎯 Sharpshooter — best accuracy (needs a clear leader, min 1 correct).
  const byAccuracy = [...players].sort((a, b) => b.correct / b.answered - a.correct / a.answered);
  if (byAccuracy[0].correct > 0) {
    give(byAccuracy[0].id, { emoji: '🎯', label: 'Sharpshooter' });
  }

  // ⚡ Fastest Finger — lowest average speed-rank among players who ever ranked.
  const ranked = players.filter(p => p.rankCount > 0);
  if (ranked.length > 0) {
    const fastest = [...ranked].sort((a, b) => a.rankSum / a.rankCount - b.rankSum / b.rankCount)[0];
    give(fastest.id, { emoji: '⚡', label: 'Fastest Finger' });
  }

  // 💀 Confidently Wrong — most wrong answers (only if they actually missed some).
  const byWrong = [...players].sort((a, b) => b.wrong - a.wrong);
  if (byWrong[0].wrong > 0) {
    give(byWrong[0].id, { emoji: '💀', label: 'Confidently Wrong' });
  }

  // 🐢 The Ponderer — slowest average rank, but only in a real crowd so it
  // doesn't just duplicate last place in a tiny game.
  if (ranked.length >= 3) {
    const slowest = [...ranked].sort((a, b) => b.rankSum / b.rankCount - a.rankSum / a.rankCount)[0];
    // Don't hand the same player Fastest and Ponderer in degenerate cases.
    if (!(result.get(slowest.id) || []).some(a => a.label === 'Fastest Finger')) {
      give(slowest.id, { emoji: '🐢', label: 'The Ponderer' });
    }
  }

  return result;
}
